import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Button,
  message,
  Spin,
  Input,
  Select,
  Tag,
  Typography,
} from "antd";
import { useState, useCallback, useMemo, useEffect } from "react";
import {
  ReloadOutlined,
  FileAddOutlined,
  SearchOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  POST_ACCEPT_CEO_RECHECK_JUDGE_DOC,
  POST_MAIN_RECHECK_JUDGE_DOC,
} from "../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";
import MotionHoc from "../../../utils/MotionHoc";

import CurrencyFormat from "../../../hook/CurrencyFormat";
import CeoReview from "./modal/CeoReview";

const { Option } = Select;

const Main = () => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  // States
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputTable, setSearchInputTable] = useState("");
  const [processStatus, setProcessStatus] = useState(20);
  const [dataRecord, setDataRecord] = useState(null);
  const [isModal, setIsModal] = useState(false);

  // Selection states
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const loadMain = useCallback(
    async (status) => {
      setLoading(true);
      setSearchInputTable("");
      try {
        const res = await axios.post(
          POST_MAIN_RECHECK_JUDGE_DOC,
          { userId, currentStatus: status },
          { headers: HEADERS_EXPORT_BEN },
        );

        if (res.data?.length > 0) {
          const sortedData = [...res.data].sort((a, b) => {
            const dateA_Ceo = a.dtCompleteCheckJudgeCeo
              ? new Date(a.dtCompleteCheckJudgeCeo)
              : 0;
            const dateB_Ceo = b.dtCompleteCheckJudgeCeo
              ? new Date(b.dtCompleteCheckJudgeCeo)
              : 0;

            if (dateA_Ceo !== dateB_Ceo) {
              return dateB_Ceo - dateA_Ceo; // เรียงจากใหม่ไปเก่า
            }

            const dateA_Acc = a.dtCompleteAcc ? new Date(a.dtCompleteAcc) : 0;
            const dateB_Acc = b.dtCompleteAcc ? new Date(b.dtCompleteAcc) : 0;

            return dateB_Acc - dateA_Acc;
          });

          console.log("sortedData", sortedData);
          setDataArr(sortedData);
        } else {
          setDataArr([]);
          message.info("ไม่มีข้อมูล");
        }
      } catch (error) {
        if (error.name !== "CanceledError") {
          message.error(
            error.response?.data?.message ||
              error.message ||
              "ไม่สามารถโหลดข้อมูลได้",
          );
          setDataArr([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [userId, processStatus],
  );

  useEffect(() => {
    if ((ROLE_ID === "5" || ROLE_ID === "1") && processStatus === 20) {
      loadMain(processStatus);
    }
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: 20, icon: "📤 ", label: " รอรับงาน" },
      { value: 17, icon: "🕒 ", label: " รอตรวจสอบ" },
      { value: 18, icon: "✅ ", label: " ตรวจสอบแล้ว" },
      { value: 13, icon: "⚖️ ", label: " ส่งอุธรณ์" },
    ],
    [],
  );

  const handleChangeStatus = useCallback(
    (value) => {
      clearSelectedRows();

      setDataArr([]);
      clearSelectedRows();
      setProcessStatus(value);
      loadMain(value);
    },
    [processStatus, dataArr],
  );

  // ✅ กรองข้อมูลอัตโนมัติด้วย useMemo
  const arrayTable = useMemo(() => {
    if (!dataArr || dataArr.length === 0) return [];

    const normalize = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase();

    const searchQuery = normalize(searchInputTable);

    return dataArr.filter((item) => {
      const passQuery =
        !searchQuery ||
        [
          item.newContno,
          item.CUSCOD,
          item.CONTNO,
          item.NAME1,
          item.NAME2,
          `${item.SNAM || ""}${item.NAME1 || ""} ${item.NAME2 || ""}`,
        ]
          .map(normalize)
          .some((val) => val.includes(searchQuery));

      return passQuery;
    });
  }, [dataArr, searchInputTable]);

  // ✅ ฟังก์ชันล้างฟิลเตอร์
  const handleResetFilter = useCallback(() => {
    setSearchInputTable("");
    message.info("ล้างตัวกรองสำเร็จ");
  }, []);

  const handleReloadJudgment = useCallback(
    (payload) => {
      clearSelectedRows();
      setDataRecord(null);
      const status = payload?.process ?? processStatus;
      loadMain(status);
    },
    [loadMain, processStatus],
  );

  const postDocStatus = async (payload, apiUse) => {
    console.log("📤 POST_DOC_STATUS with data:", payload);

    try {
      const res = await axios.post(apiUse, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }

      console.error("❌ postDocStatus error:", error);
      throw error;
    }
  };

  const handleBulkSave = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("กรุณาเลือกรายการที่ต้องการดำเนินการ");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    try {
      const selectedRecords = arrayTable.filter((item) =>
        selectedRowKeys.includes(item.CONTNO),
      );
      console.log(`📤 กำลังส่งข้อมูล ${selectedRecords.length} รายการ...`);

      for (const record of selectedRecords) {
        try {
          await postDocStatus(
            {
              loanId: record.loanId,
              lawsuitId: record.lawsuitId,
              userId: userId,
            },
            POST_ACCEPT_CEO_RECHECK_JUDGE_DOC,
          );

          successCount++;
          console.log(`✅ สำเร็จ: ${record.CONTNO}`);
        } catch (err) {
          failCount++;
          console.error(`❌ ล้มเหลว: ${record.CONTNO}`, err);
        }
      }

      if (successCount > 0) {
        message.success(
          `รับงานสำเร็จ ${successCount} รายการ${
            failCount > 0 ? `, ล้มเหลว ${failCount} รายการ` : ""
          }`,
        );
        handleReloadJudgment();
      } else {
        message.error("ไม่สามารถดำเนินการได้");
      }
    } catch (err) {
      console.error("❌ Error in bulk save:", err);
      message.error("เกิดข้อผิดพลาดในการดำเนินการ");
    } finally {
      setLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
      console.log("Selected Keys:", selectedKeys);
    },
    getCheckboxProps: (record) => ({}),
  };

  const clearSelectedRows = () => {
    setSelectedRowKeys([]);
  };

  const renderCalSumary = (record) => {
    const totalJudge = parseFloat(record.totalJugde || 0);
    const creditOld = parseFloat(record.creditDebtorOld || 0);
    const creditOld10 = creditOld * 0.1;
    const creditAll = Math.round(creditOld + creditOld10);
    const diff = totalJudge - creditAll;
    const isProfit = diff > 0 ? "green" : "red";

    return (
      <>
        <Typography style={{ color: "blue" }}>
          {currencyFormatPoint(totalJudge) +
            " - " +
            currencyFormatPoint(creditAll)}
        </Typography>
        <Typography style={{ color: diff ? isProfit : "gray" }}>
          ยอดสุทธิ: {diff ? currencyFormatPoint(diff) : "-"}
        </Typography>
      </>
    );
  };

  const renderStatus = (record) => {
    const totalJudge = parseFloat(record.totalJugde || 0);
    const creditOld = parseFloat(record.creditDebtorOld || 0);
    const diff = totalJudge - creditOld * 1.1; // ✅ ย่อ creditOld + creditOld*0.1
    const assetText =
      record.flagAsset === 1
        ? "มีทรัพย์"
        : record.flagAsset === 0
          ? "ไม่มีทรัพย์"
          : "ยังไม่สืบ";
    const assetColor =
      record.flagAsset === 1
        ? "green"
        : record.flagAsset === 0
          ? "red"
          : "default";
    return (
      <Space direction="vertical" size={2}>
        <Tag color={diff === 0 ? "white" : diff > 0 ? "success" : "error"}>
          {diff === 0 ? null : diff > 0 ? "กำไร" : "ขาดทุน"}
        </Tag>
        <Tag color={assetColor}>{assetText}</Tag>
      </Space>
    );
  };

  // ✅ Columns สำหรับตาราง
  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "เลขสัญญา",
      dataIndex: "CONTNO",
      key: "CONTNO",
      align: "center",
      width: 150,
    },
    {
      title: "เลขสัญญาใหม่",
      dataIndex: "newContno",
      key: "newContno",
      align: "center",
      width: 150,
    },
    {
      title: "ชื่อ-นามสกุล",
      key: "fullName",
      align: "center",
      render: (_, record) => (
        <span>
          {`${record.SNAM || ""}${record.NAME1 || ""} ${
            record.NAME2 || ""
          }`.trim()}
        </span>
      ),
    },
    {
      title: "ศาลให้-ทุนเก่า(+10%)",
      dataIndex: "",
      key: "",
      align: "center",
      render: (_, record) => renderCalSumary(record),
    },
    {
      title: "ผลพิจารณา",
      dataIndex: "redCaseNumber",
      key: "redCaseNumber",
      align: "center",
      render: (text, record) => {
        return (
          <div>
            <Tag
              color={record.flagLoanNew === 1 ? "green" : "red"}
              style={{ textAlign: "center" }}
            >
              {record.flagLoanNew === 1 ? "ทำยอม" : "พิพากษา"}
              {record.LAW_TYPE_ID === 6 ? "(ฟ้องส่วนต่าง)" : null}
            </Tag>
          </div>
        );
      },
    },
    {
      title: "สถานะ/ทรัพย์",
      dataIndex: "",
      key: "",
      align: "center",
      render: (_, record) => renderStatus(record),
    },
    {
      title: "วันที่ดำเนินการ",
      align: "center",
      render: (text, record) => {
        const statusConfig = {
          20: {
            field: record.dateCheckJudgeAcc,
            label: "บัญชีตรวจเมื่อ",
            color: "red",
          },
          17: {
            field: record.dtAcceptCheckJudgeCeo,
            label: "รับงานเมื่อ",
            color: "blue",
          },
          18: {
            field: record.dateCheckJudgeCeo,
            label: "ตรวจสำเร็จเมื่อ",
            color: "green",
          },
          13: {
            field: record.dtCompleteAcc,
            label: "ส่งอุธรณ์",
            color: "Brown",
          },
        };

        const config = statusConfig[record.currentStatus];

        if (!config || !config.field) {
          return <Tag color="default">-</Tag>;
        }
        return (
          <div>
            <Tag color={config.color} style={{ textAlign: "center" }}>
              {config.label}
              <br />
              {dayjs(config.field).add(543, "year").format("D MMM YY")}
            </Tag>
          </div>
        );
      },
    },
    (processStatus === 17 || processStatus === 18 || processStatus === 13) && {
      title: "การจัดการ",
      key: "actions",
      align: "center",
      width: 180,
      render: (_, record) => {
        return (
          <>
            <Button
              color={record.currentStatus === 17 ? "blue" : "green"}
              variant="outlined"
              icon={<FileAddOutlined />}
              onClick={() => {
                setDataRecord(record);
                setIsModal(true);
              }}
            >
              {record.currentStatus === 17 ? "ตรวจข้อมูล" : "ดูข้อมูล"}
            </Button>
          </>
        );
      },
    },
  ].filter(Boolean);

  return (
    <>
      <Card
        style={{
          marginBottom: 8,
          borderRadius: 12,
          border: "1px solid #ffcb05",
          background: "#fffdf0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {/* Label */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              background: "#fff3cc",
              border: "1px solid #ffcb05",
            }}
          >
            <span style={{ fontSize: 14, color: "#efa420" }}>🔍</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#efa420" }}>
              เลือกสถานะ
            </span>
          </div>

          {/* Select */}
          <Select
            size="large"
            style={{ minWidth: 240 }}
            placeholder="เลือกสถานะ"
            value={processStatus}
            onChange={handleChangeStatus}
            optionLabelProp="label"
          >
            {statusOptions.map(({ value, icon, label }) => (
              <Option key={value} value={value} label={`${icon} ${label}`}>
                <Space>
                  <span style={{ fontSize: 14 }}>{icon}</span>
                  <span>{label}</span>
                </Space>
              </Option>
            ))}
          </Select>

          {/* ✅ Tag ชิดขวา */}
          {processStatus &&
            (() => {
              const found = statusOptions.find(
                (o) => o.value === processStatus,
              );
              return found ? (
                <Tag
                  style={{
                    marginLeft: "auto", // ✅ ดันไปขวาสุด
                    fontSize: 13,
                    borderRadius: 20,
                    padding: "3px 12px",
                    background: "#fff3cc",
                    border: "1px solid #ffcb05",
                    color: "#efa420",
                    fontWeight: 500,
                  }}
                >
                  {found.icon} {found.label}
                </Tag>
              ) : null;
            })()}
        </div>
      </Card>
      <Card>
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row gutter={[0, 16]}>
            <Col span={12} style={{ textAlign: "start" }}>
              {processStatus === 20 && (
                <Button
                  type="primary"
                  ghost
                  danger
                  icon={<InboxOutlined />}
                  onClick={() => handleBulkSave()}
                  disabled={selectedRowKeys.length === 0}
                >
                  📥 รับงาน ({selectedRowKeys.length})
                </Button>
              )}
            </Col>
            <Col span={12} style={{ textAlign: "end" }}>
              <Space size={10} wrap>
                <Input
                  placeholder="ค้นหาเลขสัญญา, ชื่อ"
                  value={searchInputTable}
                  onChange={(e) => setSearchInputTable(e.target.value)}
                  style={{ width: 250, borderRadius: 8 }}
                  size="small"
                  allowClear
                  suffix={<span style={{ marginRight: 5 }}>🔍</span>}
                />

                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetFilter}
                  size="large"
                  style={{
                    borderRadius: 8,
                    backgroundColor: "#fff1f0",
                    borderColor: "#ffa39e",
                    color: "#cf1322",
                  }}
                >
                  ล้างตัวกรอง
                </Button>
              </Space>
            </Col>

            {/* Table */}
            <Col span={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={arrayTable}
                rowKey={(record) => record.CONTNO || record.id}
                scroll={{ x: 1000 }}
                rowSelection={processStatus === 20 ? rowSelection : null}
                pagination={{
                  defaultPageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                  pageSizeOptions: ["10", "20", "50", "100"],
                }}
                footer={() => (
                  <div style={{ alignItems: "center" }}>
                    <p style={{ margin: 0 }}>
                      จำนวนสัญญาทั้งหมด {arrayTable?.length}{" "}
                      {processStatus === 11 &&
                        ` | เลือก ${selectedRowKeys.length} รายการ`}
                    </p>
                  </div>
                )}
              />
            </Col>
          </Row>
        </Spin>
        {isModal && (
          <CeoReview
            open={isModal}
            close={() => setIsModal(false)}
            dataDefault={dataRecord}
            func={handleReloadJudgment}
            process={processStatus}
          />
        )}
      </Card>
    </>
  );
};

const MainReCheckJudgement = MotionHoc(Main);
export default MainReCheckJudgement;
